import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { palette } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types';

interface ScheduleSearchSheetProps {
  open: boolean;
  entries: ScheduleEntry[];
  onClose: () => void;
  onSelect: (entry: ScheduleEntry) => void;
}

export function ScheduleSearchSheet({
  open,
  entries,
  onClose,
  onSelect,
}: ScheduleSearchSheetProps) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries.slice(0, 20);
    return entries
      .filter((e) =>
        `${e.title ?? ''} ${e.location_name ?? ''} ${e.location_address ?? ''}`.toLowerCase().includes(q),
      )
      .slice(0, 20);
  }, [entries, query]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: 'rgba(15,23,42,0.35)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: 72,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 560,
          maxWidth: '92vw',
          background: palette.white,
          borderRadius: 12,
          border: `1px solid ${palette.g200}`,
          overflow: 'hidden',
          boxShadow: '0 18px 44px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, borderBottom: `1px solid ${palette.g200}` }}>
          <Search size={16} color={palette.g500} />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search schedule entries"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, color: palette.g800 }}
          />
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
            <X size={16} color={palette.g500} />
          </button>
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {filtered.map((entry) => (
            <button
              key={entry.id}
              onClick={() => { onSelect(entry); onClose(); }}
              style={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '10px 12px',
                borderBottom: `1px solid ${palette.g100}`,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: palette.g800 }}>{entry.title || 'Untitled'}</div>
              <div style={{ fontSize: 11, color: palette.g500, marginTop: 2 }}>
                {(entry.start_at ?? '').slice(0, 16).replace('T', ' ')}
                {entry.location_name ? ` • ${entry.location_name}` : ''}
              </div>
            </button>
          ))}
          {filtered.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12, color: palette.g500 }}>No matching entries.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
